import { Box, Typography } from '@mui/material';
import type { SisField, SisValue } from './sisMatchingApi';
import { formatShortDate, groupValues, plural, studentsByMeaning } from './sisMatchingView';
import { SIS, sisText } from './sisTokens';
import { MeaningMenu } from './MeaningMenu';

type Props = {
  field: SisField;
  confirmedAt?: string;
  onPick: (value: SisValue, meaning: string) => void;
};

const valueLane = { width: 200, flexShrink: 0 } as const;
const countLane = { width: 96, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

function GroupLabel({ children, split, fresh }: { children: string; split?: boolean; fresh?: boolean }) {
  return (
    <Typography sx={{ ...sisText.eyebrow, padding: '12px 16px 4px', color: fresh ? SIS.warningDark : SIS.muted, boxShadow: split ? `inset 0 1px 0 ${SIS.divider}` : 'none' }}>
      {children}
    </Typography>
  );
}

export function ValueTable({ field, confirmedAt, onPick }: Props) {
  const values = field.values ?? [];
  const meanings = field.meanings ?? [];
  if (field.kind === 'group') {
    return (
      <Box data-testid="sis-value-table" sx={{ border: `1px solid ${SIS.divider}`, borderRadius: '8px', paddingBottom: '8px' }}>
        <Header withMeaning={false} />
        {values.map((v) => (
          <Box key={v.key} data-testid="sis-value-row" sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 16px' }}>
            <Typography data-testid="sis-value-name" sx={{ ...sisText.body1Strong, ...valueLane, overflowWrap: 'anywhere' }}>{v.value}</Typography>
            <Typography sx={{ ...sisText.body1, ...countLane }}>{v.students.toLocaleString('en-US')}</Typography>
          </Box>
        ))}
      </Box>
    );
  }
  const groups = groupValues(values);
  const row = (v: SisValue) => (
    <Box key={v.key} data-testid="sis-value-row" sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 16px' }}>
      <Typography data-testid="sis-value-name" sx={{ ...(v.key === '' ? { ...sisText.body1, fontStyle: 'italic', color: SIS.muted } : sisText.body1Strong), ...valueLane, overflowWrap: 'anywhere' }}>
        {v.key === '' ? 'No value' : v.value}
      </Typography>
      <Typography sx={{ ...sisText.body1, ...countLane }}>{v.students.toLocaleString('en-US')}</Typography>
      <MeaningMenu meanings={meanings} current={v.meaning} onPick={(meaning) => onPick(v, meaning)} />
    </Box>
  );
  const totals = field.key === 'el_status' ? studentsByMeaning(values, meanings) : null;
  return (
    <>
      <Box data-testid="sis-value-table" sx={{ border: `1px solid ${SIS.divider}`, borderRadius: '8px', paddingBottom: '8px' }}>
        <Header withMeaning />
        {groups.fresh.length > 0 && (
          <Box sx={{ bgcolor: SIS.newBg, boxShadow: `inset 0 -1px 0 ${SIS.divider}`, paddingBottom: '4px' }}>
            <GroupLabel fresh>{`New since ${formatShortDate(confirmedAt)} · ${groups.fresh.length}`}</GroupLabel>
            {groups.fresh.map(row)}
          </Box>
        )}
        {groups.notMatched.length > 0 && (
          <>
            <GroupLabel>{`Not matched automatically · ${groups.notMatched.length}`}</GroupLabel>
            {groups.notMatched.map(row)}
          </>
        )}
        {groups.matched.length > 0 && (
          <>
            <GroupLabel split={groups.notMatched.length > 0}>{`Matched automatically · ${groups.matched.length}`}</GroupLabel>
            {groups.matched.map(row)}
          </>
        )}
      </Box>
      {totals && (
        <Box data-testid="sis-totals" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px 16px', borderRadius: '8px', bgcolor: SIS.totalsBg }}>
          <Typography sx={sisText.eyebrow}>Students by meaning</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px' }}>
            {totals.totals.map((t) => (
              <Box key={t.key} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ ...sisText.body1Strong, fontVariantNumeric: 'tabular-nums' }}>{t.students.toLocaleString('en-US')}</Typography>
                <Typography sx={{ ...sisText.body2, color: SIS.muted }}>{t.label}</Typography>
              </Box>
            ))}
            {totals.none > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ ...sisText.body1Strong, color: SIS.warningDark, fontVariantNumeric: 'tabular-nums' }}>{totals.none.toLocaleString('en-US')}</Typography>
                <Typography sx={{ ...sisText.body2, color: SIS.warningDark }}>No meaning yet</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

function Header({ withMeaning }: { withMeaning: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px', bgcolor: SIS.headerBg, borderRadius: '8px 8px 0 0', boxShadow: `inset 0 -1px 0 ${SIS.divider}` }}>
      <Typography sx={{ ...sisText.body2, ...valueLane }}>Value in the feed</Typography>
      <Typography sx={{ ...sisText.body2, ...countLane }}>Students</Typography>
      {withMeaning && <Typography sx={{ ...sisText.body2, width: 280, flexShrink: 0 }}>Meaning</Typography>}
    </Box>
  );
}

export function tooManyNote(field: SisField, distinct: number): string {
  return `${plural(distinct, 'different value')}. This doesn’t look like a ${field.label.toLowerCase()} column.`;
}
